import { useNavigate } from 'react-router-dom';

import { Sidebar as SidebarIcon } from '@/components/icons/Sidebar';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarHeader,
  SidebarRail,
  useSidebar
} from '@/components/ui/sidebar';

import NewChatButton from '../header/NewChat';
import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';

/**
 * Left thread-history sidebar. In `collapsible="icon"` mode it stays as a thin
 * rail when collapsed: the same toggle / new-thread / search buttons flow
 * horizontally (toggle left, search + new-chat right) when expanded and stack
 * vertically (toggle, new, search) in the rail — driven by `order`/`ml-auto`
 * `group-data-[collapsible=icon]` variants so each button renders once. Thread
 * history is hidden in the rail.
 */
export default function LeftSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate();
  const { toggleSidebar, state } = useSidebar();
  const collapsed = state === 'collapsed';
  const toggleLabel = collapsed ? 'Open sidebar' : 'Close sidebar';

  return (
    <Sidebar {...props} className="border-r border-border">
      <SidebarHeader className="py-3">
        <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={toggleLabel}
            title={toggleLabel}
          >
            <SidebarIcon className="!size-5 text-muted-foreground" />
          </Button>
          <div className="ml-auto group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:order-2">
            <SearchChats />
          </div>
          <div className="group-data-[collapsible=icon]:order-1">
            <NewChatButton navigate={navigate} />
          </div>
        </div>
      </SidebarHeader>
      <div className="flex min-h-0 flex-1 flex-col group-data-[collapsible=icon]:hidden">
        <ThreadHistory />
      </div>
      <SidebarRail />
    </Sidebar>
  );
}
